import { ColumnSeries, DateAxis, LineSeries, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, NumberFormatter } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import { connect, RootStateOrAny } from "react-redux";
import WebFont from "webfontloader";
import "../../../assets/styles/component/legend.scss";
import { CCS_ANALYSIS_COLORS, CYAN, HUMMING_BIRD, MEDIA_MIX_CHART_COLORS, ORANGE, TOP_FIVE_MODEL_EXTRA_COLOR } from "../../../constants";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import { disposeChart } from "../helpers/chartHelpers";
import { configureDateAxis, configureValueAxis, legendConfiguration, lineSeriesConfiguration } from "../helpers/configurations";
import { oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: Array<Record<string, number | string>>;
  seriesList: Array<{ name: string; field: string; type: string; color?: string }>;
  theme: ThemeInt;
  chartId: string;
  isLoading: boolean;
  barAxisIsCurrency?: boolean;
  barAxisIsPercentage?: boolean;
  barAxisMinValue?: number;
  lineAxisIsCurrency?: boolean;
  lineAxisIsPercentage?: boolean;
  sameValueAxis?: boolean;
}

const barSeriesColors = [CYAN, ORANGE];

const barSeriesBpColors = [CYAN, HUMMING_BIRD];

class MultipleBarLineTrendChart extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chart!: XYChart;
  chartId = this.props.chartId;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId).then((res) => {
        this.initChart();
      });
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  initChart() {
    const { data, seriesList, theme, barAxisIsCurrency, barAxisIsPercentage, lineAxisIsCurrency, lineAxisIsPercentage, barAxisMinValue, sameValueAxis } =
      this.props;

    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = data;
    this.chart.numberFormatter.numberFormat = "#.0a";
    this.chart.colors.list = MEDIA_MIX_CHART_COLORS.map((colorStr: string) => color(colorStr));

    // Legend configuration
    this.chart.legend = legendConfiguration(theme);
    this.chart.legend.marginBottom = 15;
    this.chart.colors.list = CCS_ANALYSIS_COLORS.map((item) => color(item));

    // Create Axes
    const dateAxis = this.chart.xAxes.push(new DateAxis());
    configureDateAxis(dateAxis, theme);
    dateAxis.renderer.minGridDistance = 10;

    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    valueAxis.numberFormatter = new NumberFormatter();
    valueAxis.numberFormatter.numberFormat = "#a";
    valueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return `${text}${barAxisIsPercentage ? "%" : ""}`;
    });
    if (barAxisMinValue != undefined) {
      valueAxis.min = barAxisMinValue;
      valueAxis.strictMinMax = true;
    }

    const lineValueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(lineValueAxis, theme);
    lineValueAxis.renderer.opposite = true;
    lineValueAxis.syncWithAxis = valueAxis;
    lineValueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return `${text}${lineAxisIsPercentage ? "%" : ""}`;
    });

    this.chart.data.sort(function (a, b) {
      //@ts-ignore
      return new Date(a.date) - new Date(b.date);
    });



    seriesList
      .filter((series) => series.type == "bar")
      ?.forEach(({ name, field, color: seriesColor }, idx) => {
        const series = this.chart.series.push(new ColumnSeries());
        series.dataFields.dateX = "date";
        series.name = name;
        series.fill = seriesColor ? color(seriesColor) : color(barSeriesColors[idx]);
        series.stroke = seriesColor ? color(seriesColor) : color(barSeriesColors[idx]);
        series.dataFields.valueY = field;
        series.xAxis = dateAxis;
        series.numberFormatter = new NumberFormatter();
        series.columns.template.tooltipText = "[bold font-size: var(--regular_font_size)]{name}: {valueY}";
      });

    seriesList
      .filter((series) => series.type == "line")
      ?.forEach(({ name, field, color: seriesColor }, idx) => {
        const series = this.chart.series.push(new LineSeries());
        series.dataFields.dateX = "date";
        series.fill = seriesColor ? color(seriesColor) : color(TOP_FIVE_MODEL_EXTRA_COLOR[idx]);
        series.stroke = seriesColor ? color(seriesColor) : color(TOP_FIVE_MODEL_EXTRA_COLOR[idx]);

        const circleBullet = lineSeriesConfiguration(series, {
          valueY: field,
          name: name,
          yAxis: sameValueAxis ? valueAxis : lineValueAxis,
        });
        circleBullet.tooltipText = `[bold font-size: var(--regular_font_size)]{name}:[bold font-size: var(--regular_font_size)] {valueY.formatNumber('###,###')}${lineAxisIsPercentage ? "%" : ""
          }`;
      });
  }

  render() {
    return (
      <>
        <div className={"chartWrapper"}>
          <div id={this.props.chartId} data-test-id={this.props.chartId} className={"graph"} />
          <LoadingEllipsis isLoading={this.props.isLoading} />
        </div>
      </>
    );
  }
}

function mapStateToProps(state: RootStateOrAny) {
  const currency = state.journey_excellence.currency;
  return {
    currency,
  };
}

export default connect(mapStateToProps)(MultipleBarLineTrendChart);
